import { forwardRef, ReactNode } from "react";
import { CheckboxGroup, CheckboxGroupProps } from "../Checkbox/CheckboxGroup";
import { RadioGroup } from "../Radio/RadioGroup";
import { ISelectCardContext, SelectCardProvider } from "./SelectCardProvider";

/**
 * Props for the SelectCardGroup component
 * @extends Omit<ISelectCardContext, "radioName" | "selection">
 */
export interface SelectCardGroupProps
  extends Omit<ISelectCardContext, "radioName" | "selection"> {
  /**
   * Associates the legend with content below.
   */
  ariaLabelledBy?: string;

  children?: ReactNode;

  /**
   * Sets the legend on the group.
   */
  legend: CheckboxGroupProps["legend"];

  /**
   * When `true`, sets "required" text in label.
   * @accessibility This does not enforce error when nothing is selected.
   * @default false
   */
  required?: boolean;
}

/**
 * SelectCardGroup component for managing groups of selectable cards.
 *
 * Features:
 * - Manages selection state for multiple SelectCard components
 * - Supports both single and multiple selection modes
 * - Provides proper accessibility with legends and ARIA labels
 * - Handles required field validation
 * - Automatic radio button grouping for single selection
 * - Context provider for child SelectCard components
 * - Integrates with CheckboxGroup and RadioGroup for proper form semantics
 *
 * @example
 * <SelectCardGroup
 *   legend="Choose your plan"
 *   selectionMode="single"
 *   onChange={(e, state) => console.log('Selection changed:', state)}
 * >
 *   <SelectCard id="basic" onChange={() => {}}>
 *     <Text variant="headline" size="small" el="h3">Basic Plan</Text>
 *   </SelectCard>
 *   <SelectCard id="premium" onChange={() => {}}>
 *     <Text variant="headline" size="small" el="h3">Premium Plan</Text>
 *   </SelectCard>
 * </SelectCardGroup>
 */
export const SelectCardGroup = forwardRef<
  HTMLInputElement,
  SelectCardGroupProps
>((props, _ref) => {
  const {
    ariaLabelledBy,
    children,
    legend,
    onChange,
    required = false,
    selectionMode,
    ...rest
  } = props;

  return (
    <SelectCardProvider onChange={onChange} selectionMode={selectionMode}>
      {selectionMode === "multiple" && (
        <CheckboxGroup
          aria-labelledby={ariaLabelledBy}
          legend={legend}
          required={required}
          {...rest}
        >
          {children}
        </CheckboxGroup>
      )}
      {selectionMode === "single" && (
        <RadioGroup
          aria-labelledby={ariaLabelledBy}
          legend={legend}
          required={required}
          {...rest}
        >
          {children}
        </RadioGroup>
      )}
    </SelectCardProvider>
  );
});

SelectCardGroup.displayName = "SelectCardGroup";
